import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { MatError, MatLabel } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { ToastrService } from "ngx-toastr";
import { ApplicationService } from "../../../services/application.service";
import { MatSpinner } from "@angular/material/progress-spinner";
import { NgxDocViewerModule } from "ngx-doc-viewer";

@Component({
  selector: "app-ng-fileupload",
  standalone: true,
  templateUrl: "./ng-fileupload.component.html",
  styleUrl: "./ng-fileupload.component.scss",
  imports: [
    CommonModule,
    MatIconModule,
    MatError,
    MatLabel,
    ReactiveFormsModule,
    MatSpinner,
    NgxDocViewerModule,
  ],
})
export class NgFileuploadComponent implements OnInit {
  @Input() fileName?: string; // Optional, for display
  @Input() fileBlobUrl?: string;
  @Input() isMultiple: boolean = false;
  @Input() readonly: boolean = false;
  @Input() labelText: string = ""; // Dynamic text for the <p> tag
  @Input() fileId: string = ""; // Unique identifier for different file inputs
  @Output() filesSelected: EventEmitter<{ id: string; files: File[] }> =
    new EventEmitter<{ id: string; files: File[] }>();
  @Input() control: FormControl = new FormControl<File | null>(null);
  @Input() error: string = "...";

  files: File[] = [];
  showPreview = false;
  previewUrl: SafeResourceUrl = "";
  previewFileType: "image" | "doc" | "pdf" | null = null;
  isLoading: boolean = false;
  scale = 1;
  minScale = 1;
  maxScale = 6;
  transform = 'scale(1)';
  targetScale = 1;
  animationFrameId: any;
  velocityX = 0;
  velocityY = 0;
  momentumId: any;

  ngOnInit() {
    if (this.fileBlobUrl) {
      this.previewUrl = this.sanitizeUrl(this.fileBlobUrl);
    }
  }
  constructor(
    private toast: ToastrService,
    private sanitizer: DomSanitizer,
    public applicationService: ApplicationService
  ) { }

  togglePreview(): void {
    if (this.showPreview) {
      this.showPreview = false;
      this.previewUrl = "";
      this.previewFileType = null;
      return;
    }

    let file: File | null = null;

    if (this.files.length > 0) {
      file = this.files[0];
      const url = URL.createObjectURL(file);
      this.previewUrl = this.sanitizeUrl(url);
      this.previewFileType = this.getFileType(file.name);
    } else if (this.fileBlobUrl) {
      this.previewUrl = this.sanitizeUrl(this.fileBlobUrl);
      this.previewFileType = this.getFileType(this.fileName || "default.file");
    }
    this.showPreview = !!this.previewUrl;
  }

  getFileType(fileName: string): "image" | "pdf" | "doc" | null {
    const extension = fileName.split(".").pop()?.toLowerCase();
    if (!extension) return null;
    if (["jpg", "jpeg", "png"].includes(extension)) return "image";
    if (["pdf"].includes(extension)) return "pdf";
    if (["doc", "docx"].includes(extension)) return "doc";
    return null;
  }

  sanitizeUrl(url: string): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }
  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      const allowedTypes = ["jpg", "jpeg", "png", "pdf", "doc", "docx"];
      const selectedFiles = Array.from(input.files);
      // const MAX_SIZE_MB = 40;
      // const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;
      this.previewFileType = this.getFileType(selectedFiles[0].name);

      for (const file of selectedFiles) {
        console.log(
          `File selected: ${file.name}, Size: ${(
            file.size /
            (1024 * 1024)
          ).toFixed(2)} MB`
        );
        const fileExtension = file.name.split(".").pop()?.toLowerCase();
        if (!fileExtension || !allowedTypes.includes(fileExtension)) {
          this.toast.error(
            `Invalid file type: ${file.name} Only .jpeg,.jpg,.png & .pdf`,
            "error",
            {
              positionClass: "toast-bottom-center",
            }
          );
          return;
        }

      }

      this.files = selectedFiles;
      this.filesSelected.emit({ id: this.fileId, files: this.files });
      this.control.markAsTouched(); // Mark the control as touched for validation
    }
  }
  removeFile(): void {
    this.files.splice(0, 1);
    this.control.setValue(null); // optional: reset form control
    this.control.markAsTouched();

    // If no files left, close preview and optionally fallback to fileUrl
    if (this.files.length === 0) {
      this.showPreview = false;
      this.previewUrl = "";
      this.previewFileType = null;
    }
  }
  downloadFile(): void {
    let url = "";
    if (this.files.length > 0) {
      url = URL.createObjectURL(this.files[0]);
    } else if (this.fileBlobUrl) {
      url = this.convertToHttps(this.fileBlobUrl);
    }

    const a = document.createElement("a");
    a.href = url;
    a.download = this.fileName || this.files[0].name;
    a.click();
  }

  convertToHttps(url: any) {
    if (url.startsWith("http://")) {
      return url.replace("http://", "https://");
    } else {
      return url;
    }
  }




  zoomIn(wrapper: HTMLElement) {
    this.zoomAtPoint(wrapper, wrapper.clientWidth / 2, wrapper.clientHeight / 2, 1.2);
  }

  zoomOut(wrapper: HTMLElement) {
    this.zoomAtPoint(wrapper, wrapper.clientWidth / 2, wrapper.clientHeight / 2, 1 / 1.2);
  }

  resetZoom() {
    this.scale = 1;
    // this.transformOrigin = 'center center';
    this.updateTransform();
  }

  updateTransform() {
    this.transform = `scale(${this.scale})`;
  }
  onWheel(event: WheelEvent) {
    event.preventDefault();

    const wrapper = event.currentTarget as HTMLElement;

    const rect = wrapper.getBoundingClientRect();

    const mouseX = event.clientX - rect.left + wrapper.scrollLeft;
    const mouseY = event.clientY - rect.top + wrapper.scrollTop;

    const previousScale = this.scale;

    const zoomIntensity = 0.0015; // smooth like maps
    const delta = -event.deltaY;

    const zoom = Math.exp(delta * zoomIntensity);

    this.scale *= zoom;
    this.scale = Math.min(Math.max(this.scale, this.minScale), this.maxScale);

    const scaleRatio = this.scale / previousScale;

    wrapper.scrollLeft = mouseX * scaleRatio - (event.clientX - rect.left);
    wrapper.scrollTop = mouseY * scaleRatio - (event.clientY - rect.top);

    this.transform = `scale(${this.scale})`;
  }
  animateZoom() {
    if (Math.abs(this.scale - this.targetScale) < 0.001) return;

    this.scale += (this.targetScale - this.scale) * 0.2; // easing factor
    this.transform = `scale(${this.scale})`;

    this.animationFrameId = requestAnimationFrame(() => this.animateZoom());
  }

  zoomAtPoint(wrapper: HTMLElement, offsetX: number, offsetY: number, zoomFactor: number) {
    const previousScale = this.scale;
    this.targetScale = Math.min(Math.max(this.scale * zoomFactor, this.minScale), this.maxScale);

    const scaleRatio = this.targetScale / previousScale;

    wrapper.scrollLeft = (wrapper.scrollLeft + offsetX) * scaleRatio - offsetX;
    wrapper.scrollTop = (wrapper.scrollTop + offsetY) * scaleRatio - offsetY;

    cancelAnimationFrame(this.animationFrameId);
    this.animateZoom();
  }


}
